import {
  VIOLATION_DIRECT_PROVENANCE_FILTER,
  VIOLATION_NOT_DUPLICATE_FILTER,
  VIOLATION_STRUCTURAL_FILTERS,
} from "encamp-shared/src/violations/violationStructuralFilters";
import { HarmonizedViolationResolutionStatus } from "generated-graphql/graphql";
import { addOrUpdateKeyInOmnisearch, removeKeyInOmnisearch } from "utils/omnisearchUtils";

/**
 * EPA's own status, as the landing view and the "Show only unresolved" toggle
 * ask for it. Matches a `HarmonizedViolationResolutionStatus`.
 */
export const UNRESOLVED_STATUS = HarmonizedViolationResolutionStatus.Unresolved;

/**
 * The user-tracked "Resolution status" as a synthetic "not resolved by the user"
 * value — govStatus has no UNRESOLVED member, so the backend maps this to
 * "govStatus is neither terminal status" (RESOLVED or CLOSED_WONT_DO),
 * including the untouched rows.
 */
export const GOV_UNRESOLVED = "UNRESOLVED";

/** Current-owner matches only; former-owner (shared-FRS) rows carry another value. */
export const DIRECT_PROVENANCE = VIOLATION_DIRECT_PROVENANCE_FILTER[1];

/** `duplicateOfId is null` — the rows a user has NOT marked as a duplicate. */
export const NOT_DUPLICATE = VIOLATION_NOT_DUPLICATE_FILTER[1];

/**
 * The grid's fresh-landing filters, key and value. ONE definition, read by
 * everything that applies them (the default-filter-on-mount, and the held
 * toggle's off-switch restoring what the on-switch cleared) and by everything
 * that clears them ({@link duplicatesOfOmnisearch}) — so a fifth default cannot
 * be applied in one place and silently missed in another.
 *
 * The last two come from `encamp-shared`, where the facility fetch also reads
 * them to scope its per-facility totals.
 */
export const VIOLATION_LANDING_FILTERS: [string, string][] = [
  ["status", UNRESOLVED_STATUS],
  ["govStatus", GOV_UNRESOLVED],
  ...VIOLATION_STRUCTURAL_FILTERS.map(([key, value]): [string, string] => [key, value]),
];

/**
 * The keys those defaults occupy. A drill-down that must show a specific,
 * already-counted set of rows has to clear them: the user never set them and
 * cannot see them, so a row they suppress reads as a miscount rather than as a
 * filter.
 */
export const VIOLATION_LANDING_FILTER_KEYS = VIOLATION_LANDING_FILTERS.map(([key]) => key);

/**
 * The omnisearch showing exactly the rows a canonical's duplicate badge counted:
 * `duplicateOf:<canonical id>`, with the landing defaults cleared. `duplicate`
 * goes with them — the landing `duplicate:false` contradicts `duplicateOf`
 * outright, and `duplicate:true` would add nothing to it.
 *
 * Filters the user set themselves are kept. They stay visible as pills, so a
 * narrower list is explicable; the cleared keys are exactly the invisible ones.
 *
 * Returns one string rather than chaining key edits: each omnisearch action
 * recomputes from the URL snapshot, so two writes in a tick clobber each other.
 */
export function duplicatesOfOmnisearch(search: string, canonicalId: string): string {
  let next = search;
  for (const key of VIOLATION_LANDING_FILTER_KEYS) {
    next = removeKeyInOmnisearch(next, key);
  }
  return addOrUpdateKeyInOmnisearch(next, "duplicateOf", canonicalId);
}
